import { Signal } from '../../../../types/signals-live';

export interface ConductFeedProgressState {
  stage: string;
  percent: number;
  ts: number;
  startedAt: number;
  lastRealtimeAt: number;
}

export interface ConductFeedRealtimeState {
  detailCache: Record<string, any>;
  signals: Signal[];
}

export interface ConductFeedRealtimeMessage {
  type?: string;
  signal_id?: string;
  timestamp?: number;
  data?: any;
}

export interface ConductFeedRealtimeOptions {
  now: number;
  fallbackSignalId?: string;
}

export interface ConductFeedRealtimeResult extends ConductFeedRealtimeState {
  classifiedSignalId?: string;
}

const COMPLETION_MESSAGE_TYPES = new Set([
  'signal.updated',
  'signal.classified',
  'signal.processed',
  'signal.processed_with_pipeline',
  'signal.processed_legacy',
]);

export function applySignalRealtimeMessage(
  state: ConductFeedRealtimeState,
  message: ConductFeedRealtimeMessage,
  options: ConductFeedRealtimeOptions
): ConductFeedRealtimeResult {
  const signalId = getRealtimeSignalId(message, options.fallbackSignalId);
  if (!message.type || !signalId || !message.data) {
    return state;
  }

  if (message.type === 'signal.progress') {
    return applyProgressMessage(state, signalId, message, options.now);
  }

  if (COMPLETION_MESSAGE_TYPES.has(message.type)) {
    return applyCompletionMessage(state, signalId, message, options.now);
  }

  return state;
}

export function getRealtimeClassificationSignalId(
  message: ConductFeedRealtimeMessage,
  fallbackSignalId?: string
): string | undefined {
  if (message.type !== 'signal.classified' || !message.data) return undefined;
  return getRealtimeSignalId(message, fallbackSignalId);
}

export function buildProgressState(
  prev: ConductFeedProgressState | undefined,
  update: {
    stage?: string;
    percent?: number;
    timestamp: number;
    markDone?: boolean;
  }
): ConductFeedProgressState {
  const startedAt = prev?.startedAt ?? update.timestamp;
  const stage = update.markDone ? 'done' : update.stage ?? prev?.stage ?? 'processing';
  const percent = update.markDone ? 100 : update.percent ?? prev?.percent ?? 15;

  return {
    stage,
    percent,
    ts: update.timestamp,
    startedAt,
    lastRealtimeAt: update.timestamp,
  };
}

function applyProgressMessage(
  state: ConductFeedRealtimeState,
  signalId: string,
  message: ConductFeedRealtimeMessage,
  now: number
): ConductFeedRealtimeResult {
  const data = message.data;
  const realtimeTimestamp = data.lastRealtimeAt || message.timestamp || now;
  const startedAt = data.startedAt || now;

  return {
    detailCache: updateDetail(state.detailCache, signalId, (current) => {
      const prevProgress = current._progress;
      return {
        ...current,
        processing_status: data.processing_status || current.processing_status || 'pending',
        _progress: buildProgressState(prevProgress ?? { startedAt, ts: now }, {
          stage: data.stage,
          percent: typeof data.percent === 'number' ? data.percent : prevProgress?.percent,
          timestamp: realtimeTimestamp,
        }),
      };
    }),
    signals: state.signals.map((signal) =>
      signal.id === signalId
        ? { ...signal, processing_status: data.processing_status || signal.processing_status }
        : signal
    ),
  };
}

function applyCompletionMessage(
  state: ConductFeedRealtimeState,
  signalId: string,
  message: ConductFeedRealtimeMessage,
  now: number
): ConductFeedRealtimeResult {
  const data = message.data;
  const markDone = shouldMarkDone(data);

  return {
    detailCache: updateDetail(state.detailCache, signalId, (current) => {
      const prevProgress = current._progress;
      const merged = { ...current, ...data };

      if (markDone) {
        delete merged.processing_status;
      } else if (data.processing_status) {
        merged.processing_status = data.processing_status;
      }

      const nextProgress = markDone
        ? buildProgressState(prevProgress, { timestamp: now, markDone: true })
        : prevProgress || data.processing_status === 'pending'
        ? buildProgressState(prevProgress ?? { startedAt: now, ts: now }, {
            timestamp: now,
            stage: data.stage ?? prevProgress?.stage,
            percent: typeof data.percent === 'number' ? data.percent : undefined,
          })
        : prevProgress;

      if (nextProgress) {
        merged._progress = nextProgress;
      } else {
        delete merged._progress;
      }

      return merged;
    }),
    signals: state.signals.map((signal) => {
      if (signal.id !== signalId) return signal;
      const merged: any = { ...signal, ...data };
      if (markDone) {
        delete merged.processing_status;
      } else if (data.processing_status) {
        merged.processing_status = data.processing_status;
      }
      return merged;
    }),
    classifiedSignalId:
      message.type === 'signal.classified' ? signalId : undefined,
  };
}

function updateDetail(
  detailCache: Record<string, any>,
  signalId: string,
  updater: (current: any) => any
): Record<string, any> {
  return {
    ...detailCache,
    [signalId]: updater(detailCache[signalId] || {}),
  };
}

function shouldMarkDone(data: any): boolean {
  return (
    data.processing_status === 'done' ||
    (!hasOwn(data, 'processing_status') && Boolean(data.classification || data.taxonomy_classification))
  );
}

function getRealtimeSignalId(
  message: ConductFeedRealtimeMessage,
  fallbackSignalId?: string
): string | undefined {
  return message.signal_id || fallbackSignalId;
}

function hasOwn(value: any, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}
